import type { OpenWorldPreparation } from '@/lib/openWorlds/preparation'
import type { OpenWorldNoteTurn } from '@/lib/openWorlds/notes'
import { validateOpenWorldConfiguration } from '@/lib/openWorlds/configuration'
import type { OpenWorldConfiguration } from '@/lib/openWorlds/types'
import type { OpenWorldConversationSettings } from '@/lib/openWorlds/conversation'

export const OPEN_WORLD_ATTEMPT_LIMITS = {
  userMessageBytes: 64 * 1024,
  partialTextBytes: 256 * 1024,
  errorMessageBytes: 4 * 1024,
  postgresIntegerMax: 2_147_483_647,
  listLimit: 50,
} as const

export type OpenWorldAttemptIntent = 'advance' | 'reroll' | 'retry'
export type OpenWorldAttemptStatus =
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'interrupted'
export type OpenWorldAttemptTerminalStatus = 'failed' | 'cancelled'

export interface OpenWorldAttemptError {
  code: string
  message: string
}

export interface OpenWorldResolvedBotIds {
  storyteller: number
  curator: number
  synthesis: number
}

export interface OpenWorldAttemptDetail {
  conversationSettings?: OpenWorldConversationSettings
  id: string
  playthroughId: string
  parentAttemptId: string | null
  baseHeadId: string | null
  sourceAttemptId: string | null
  intent: OpenWorldAttemptIntent
  turnNumber: number
  claimedRevision: number
  userMessage: string
  definitionRevision: number
  configuration: OpenWorldConfiguration
  resolvedBotIds: OpenWorldResolvedBotIds
  status: OpenWorldAttemptStatus
  partialText: string
  checkpointSequence: number
  leaseExpiresAt: string
  hardDeadlineAt: string
  error: OpenWorldAttemptError | null
  createdAt: string
  finishedAt: string | null
}

export type OpenWorldAttemptSummary = Pick<
  OpenWorldAttemptDetail,
  | 'id'
  | 'playthroughId'
  | 'parentAttemptId'
  | 'baseHeadId'
  | 'sourceAttemptId'
  | 'intent'
  | 'turnNumber'
  | 'claimedRevision'
  | 'userMessage'
  | 'definitionRevision'
  | 'status'
  | 'partialText'
  | 'checkpointSequence'
  | 'leaseExpiresAt'
  | 'hardDeadlineAt'
  | 'error'
  | 'createdAt'
  | 'finishedAt'
>

export interface PlaythroughExecutionState {
  id: string
  revision: number
  activeHeadId: string | null
  pendingAttemptId: string | null
  updatedAt: string
}

export interface OpenWorldAttemptAcknowledgement {
  attemptId: string
  status: 'failed' | 'cancelled' | 'interrupted'
  finishedAt: string
  playthroughRevision: number
  activeHeadId: string | null
}

export interface BeginOpenWorldAttemptRequest {
  playerMessageId: string
  id: string
  playthroughId: string
  executionToken: string
  expectedPlaythroughRevision: number
  expectedHeadId: string | null
  expectedDefinitionRevision: number
  intent: 'advance'
  sourceAttemptId: null
  userMessage: string
}

export interface BeginOpenWorldAttemptResponse {
  canonCaptured?: boolean
  noteTurn?: OpenWorldNoteTurn | null
  preparation?: OpenWorldPreparation | null
  attempt: OpenWorldAttemptDetail
  playthroughState: PlaythroughExecutionState
  replayed: boolean
}

export interface RenewOpenWorldAttemptLeaseRequest {
  attemptId: string
  executionToken: string
}

export interface RenewOpenWorldAttemptLeaseResponse {
  attemptId: string
  leaseExpiresAt: string
  hardDeadlineAt: string
  serverNow: string
}

export interface CheckpointOpenWorldAttemptRequest extends RenewOpenWorldAttemptLeaseRequest {
  sequence: number
  partialText: string
}

export interface CheckpointOpenWorldAttemptResponse {
  attemptId: string
  sequence: number
  replayed: boolean
}

export interface TerminateOpenWorldAttemptRequest extends RenewOpenWorldAttemptLeaseRequest {
  receiptId: string
  status: OpenWorldAttemptTerminalStatus
  partialText: string
  error: OpenWorldAttemptError | null
}

export interface RecoverOpenWorldAttemptRequest {
  attemptId: string
  receiptId: string
  expectedPlaythroughRevision: number
}

export interface OpenWorldAttemptTerminalResponse {
  acknowledgement: OpenWorldAttemptAcknowledgement
  playthroughState: PlaythroughExecutionState
  replayed: boolean
}

export interface GetOpenWorldAttemptResponse {
  attempt: OpenWorldAttemptDetail | null
}

export interface OpenWorldAttemptCursor {
  createdAt: string
  id: string
}

export interface ListOpenWorldAttemptsRequest {
  playthroughId: string
  limit?: number
  cursor?: OpenWorldAttemptCursor | null
}

export interface ListOpenWorldAttemptsResponse {
  items: OpenWorldAttemptSummary[]
  nextCursor: OpenWorldAttemptCursor | null
}

export interface OpenWorldAttemptValidationIssue {
  path: string
  message: string
}

export type OpenWorldAttemptValidationResult<T> =
  | { success: true; value: T }
  | { success: false; issues: OpenWorldAttemptValidationIssue[] }

type UnknownRecord = Record<string, unknown>
const encoder = new TextEncoder()
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,6})?(?:Z|[+-]\d{2}:\d{2})$/
const ERROR_CODE_RE = /^[a-z][a-z0-9_]{0,63}$/
const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const hasOwn = (value: UnknownRecord, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key)
const bytes = (value: string): number => encoder.encode(value).byteLength

function issue(path: string, message: string): OpenWorldAttemptValidationResult<never> {
  return { success: false, issues: [{ path, message }] }
}

function exact(value: unknown, keys: readonly string[], path: string): value is UnknownRecord {
  if (!isRecord(value)) return false
  const actual = Object.keys(value)
  return actual.length === keys.length && keys.every((key) => hasOwn(value, key))
}

function uuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_RE.test(value)
}

function timestamp(value: unknown): value is string {
  return typeof value === 'string' && TIMESTAMP_RE.test(value) && Number.isFinite(Date.parse(value))
}

function positiveInteger(value: unknown): value is number {
  return (
    Number.isInteger(value) &&
    (value as number) >= 1 &&
    (value as number) <= OPEN_WORLD_ATTEMPT_LIMITS.postgresIntegerMax
  )
}

function nullableUuid(value: unknown): value is string | null {
  return value === null || uuid(value)
}

function validateError(
  value: unknown,
  path: string,
): OpenWorldAttemptValidationResult<OpenWorldAttemptError | null> {
  if (value === null) return { success: true, value: null }
  if (!exact(value, ['code', 'message'], path))
    return issue(path, 'Must contain exactly code and message.')
  if (typeof value.code !== 'string' || !ERROR_CODE_RE.test(value.code)) {
    return issue(`${path}.code`, 'Must be a safe error code.')
  }
  if (
    typeof value.message !== 'string' ||
    bytes(value.message) > OPEN_WORLD_ATTEMPT_LIMITS.errorMessageBytes
  ) {
    return issue(`${path}.message`, 'Must be at most 4096 UTF-8 bytes.')
  }
  return { success: true, value: { code: value.code, message: value.message } }
}

function validateBaseRequest<T>(
  value: unknown,
  keys: readonly string[],
  build: (record: UnknownRecord) => OpenWorldAttemptValidationResult<T>,
): OpenWorldAttemptValidationResult<T> {
  if (!exact(value, keys, 'request'))
    return issue('request', 'Must contain exactly the documented fields.')
  return build(value)
}

export function validateBeginOpenWorldAttemptRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<BeginOpenWorldAttemptRequest> {
  return validateBaseRequest(
    value,
    [
      'id',
      'playthroughId',
      'executionToken',
      'expectedPlaythroughRevision',
      'expectedHeadId',
      'expectedDefinitionRevision',
      'intent',
      'sourceAttemptId',
      'userMessage',
      'playerMessageId',
    ],
    (record) => {
      for (const key of ['id', 'playthroughId', 'executionToken', 'playerMessageId'] as const) {
        if (!uuid(record[key])) return issue(key, 'Must be a UUID.')
      }
      if (!positiveInteger(record.expectedPlaythroughRevision))
        return issue('expectedPlaythroughRevision', 'Must be a positive PostgreSQL integer.')
      if (!nullableUuid(record.expectedHeadId))
        return issue('expectedHeadId', 'Must be a UUID or null.')
      if (!positiveInteger(record.expectedDefinitionRevision))
        return issue('expectedDefinitionRevision', 'Must be a positive PostgreSQL integer.')
      if (record.intent !== 'advance') return issue('intent', 'Must be advance; resend creates a fresh attempt for a saved message.')
      if (record.sourceAttemptId !== null) return issue('sourceAttemptId', 'Must be null.')
      if (typeof record.userMessage !== 'string' || !record.userMessage.trim())
        return issue('userMessage', 'Must be a nonblank string.')
      if (bytes(record.userMessage) > OPEN_WORLD_ATTEMPT_LIMITS.userMessageBytes)
        return issue('userMessage', 'Must be at most 65536 UTF-8 bytes.')
      return {
        success: true,
        value: structuredClone(record) as unknown as BeginOpenWorldAttemptRequest,
      }
    },
  )
}

export function validateRenewOpenWorldAttemptLeaseRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<RenewOpenWorldAttemptLeaseRequest> {
  return validateBaseRequest(value, ['attemptId', 'executionToken'], (record) => {
    if (!uuid(record.attemptId)) return issue('attemptId', 'Must be a UUID.')
    if (!uuid(record.executionToken)) return issue('executionToken', 'Must be a UUID.')
    return {
      success: true,
      value: { attemptId: record.attemptId, executionToken: record.executionToken },
    }
  })
}

export function validateCheckpointOpenWorldAttemptRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<CheckpointOpenWorldAttemptRequest> {
  return validateBaseRequest(
    value,
    ['attemptId', 'executionToken', 'sequence', 'partialText'],
    (record) => {
      if (!uuid(record.attemptId)) return issue('attemptId', 'Must be a UUID.')
      if (!uuid(record.executionToken)) return issue('executionToken', 'Must be a UUID.')
      if (!positiveInteger(record.sequence))
        return issue('sequence', 'Must be a positive PostgreSQL integer.')
      if (
        typeof record.partialText !== 'string' ||
        bytes(record.partialText) > OPEN_WORLD_ATTEMPT_LIMITS.partialTextBytes
      )
        return issue('partialText', 'Must be at most 262144 UTF-8 bytes.')
      return {
        success: true,
        value: structuredClone(record) as unknown as CheckpointOpenWorldAttemptRequest,
      }
    },
  )
}

export function validateTerminateOpenWorldAttemptRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<TerminateOpenWorldAttemptRequest> {
  return validateBaseRequest(
    value,
    ['attemptId', 'executionToken', 'receiptId', 'status', 'partialText', 'error'],
    (record) => {
      for (const key of ['attemptId', 'executionToken', 'receiptId'] as const)
        if (!uuid(record[key])) return issue(key, 'Must be a UUID.')
      if (record.status !== 'failed' && record.status !== 'cancelled')
        return issue('status', 'Must be failed or cancelled.')
      if (
        typeof record.partialText !== 'string' ||
        bytes(record.partialText) > OPEN_WORLD_ATTEMPT_LIMITS.partialTextBytes
      )
        return issue('partialText', 'Must be at most 262144 UTF-8 bytes.')
      const error = validateError(record.error, 'error')
      if (!error.success) return error
      if (record.status === 'failed' && error.value === null)
        return issue('error', 'Failed attempts require an error.')
      return {
        success: true,
        value: {
          attemptId: record.attemptId as string,
          executionToken: record.executionToken as string,
          receiptId: record.receiptId as string,
          status: record.status,
          partialText: record.partialText,
          error: error.value,
        },
      }
    },
  )
}

export function validateRecoverOpenWorldAttemptRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<RecoverOpenWorldAttemptRequest> {
  return validateBaseRequest(
    value,
    ['attemptId', 'receiptId', 'expectedPlaythroughRevision'],
    (record) => {
      if (!uuid(record.attemptId)) return issue('attemptId', 'Must be a UUID.')
      if (!uuid(record.receiptId)) return issue('receiptId', 'Must be a UUID.')
      if (!positiveInteger(record.expectedPlaythroughRevision))
        return issue('expectedPlaythroughRevision', 'Must be a positive PostgreSQL integer.')
      return {
        success: true,
        value: {
          attemptId: record.attemptId,
          receiptId: record.receiptId,
          expectedPlaythroughRevision: record.expectedPlaythroughRevision,
        },
      }
    },
  )
}

export function validateListOpenWorldAttemptsRequest(
  value: unknown,
): OpenWorldAttemptValidationResult<
  Required<Pick<ListOpenWorldAttemptsRequest, 'playthroughId' | 'limit'>> & {
    cursor: OpenWorldAttemptCursor | null
  }
> {
  if (!isRecord(value)) return issue('request', 'Must be an object.')
  if (
    Object.keys(value).some((key) => !['playthroughId', 'limit', 'cursor'].includes(key)) ||
    !hasOwn(value, 'playthroughId')
  )
    return issue('request', 'Contains missing or unknown fields.')
  if (!uuid(value.playthroughId)) return issue('playthroughId', 'Must be a UUID.')
  const limit = value.limit === undefined ? 20 : value.limit
  if (
    !Number.isInteger(limit) ||
    (limit as number) < 1 ||
    (limit as number) > OPEN_WORLD_ATTEMPT_LIMITS.listLimit
  )
    return issue('limit', 'Must be an integer from 1 through 50.')
  let cursor: OpenWorldAttemptCursor | null = null
  if (value.cursor !== undefined && value.cursor !== null) {
    if (!exact(value.cursor, ['createdAt', 'id'], 'cursor'))
      return issue('cursor', 'Must contain exactly createdAt and id.')
    if (!timestamp(value.cursor.createdAt))
      return issue('cursor.createdAt', 'Must be an RFC 3339 timestamp with a timezone.')
    if (!uuid(value.cursor.id)) return issue('cursor.id', 'Must be a UUID.')
    cursor = { createdAt: value.cursor.createdAt, id: value.cursor.id }
  }
  return {
    success: true,
    value: { playthroughId: value.playthroughId, limit: limit as number, cursor },
  }
}

export const isOpenWorldAttemptUuid = uuid
export const isOpenWorldAttemptTimestamp = timestamp
export const isOpenWorldAttemptPositiveInteger = positiveInteger
export const isOpenWorldAttemptNullableUuid = nullableUuid
export const validateOpenWorldAttemptError = validateError
export const isOpenWorldAttemptRecord = isRecord
export const hasOpenWorldAttemptOwnKey = hasOwn

export function validateOpenWorldAttemptConfiguration(
  value: unknown,
): OpenWorldConfiguration | null {
  const result = validateOpenWorldConfiguration(value)
  return result.success ? structuredClone(result.value) : null
}
